/**
 * SecureFileOnboardingStep — single-step setup for the Secure Information File.
 *
 * SDE Phase 2 (s88). Redesigned from the original 4-screen flow (session 27) to a single
 * step: an optional passphrase + a free-text "where will you store this file?" location.
 * Triggered by SensitiveFieldInput when the planner first focuses a Category A field and
 * setup is not yet complete.
 *
 * On "Create secure file": builds + saves the encrypted .oesif from the in-memory store
 * (saveSecureFile — picker on Chrome/Edge, download on Safari/Firefox), marks the store
 * values saved, and hands the handle + passphrase + location to the caller (which records
 * setup + persists the non-sensitive location server-side). Phase 3 will add the
 * file-open/new-device branch.
 */

import React, { useState, useCallback } from "react";
import zxcvbn from "zxcvbn";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Lock, Eye, EyeOff } from "lucide-react";
import { useSensitiveDataSession } from "@/lib/sensitive-data-session-provider";
import { saveSecureFile, NO_PASSPHRASE_KEY } from "@/lib/secure-file-save";
import { saveHandle } from "@/lib/secure-file-handle-store";

export interface SecureFileOnboardingProps {
  /** Display name of the field that triggered the setup (e.g. "Social Insurance Number"). */
  fieldName: string;
  /** Called after the file is created. handle is null on Safari/Firefox; location is the
   *  planner's free-text description (persisted server-side by the caller). */
  onComplete: (handle: FileSystemFileHandle | null, passphrase: string, location?: string) => void;
  /** Called when the planner clicks "Skip for now". */
  onSkip: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** s111: descriptive filename for the new .oesif (name + creation date). Undefined →
   *  the saveSecureFile default. */
  fileName?: string;
}

const STRENGTH_LABELS = ["Weak", "Weak", "Moderate", "Strong", "Very strong"] as const;
const STRENGTH_COLORS = ["bg-red-500", "bg-red-500", "bg-amber-500", "bg-teal-500", "bg-[#4F6B51]"] as const;

function StrengthMeter({ passphrase }: { passphrase: string }) {
  if (!passphrase) return null;
  const score = zxcvbn(passphrase).score as 0 | 1 | 2 | 3 | 4;
  return (
    <div className="space-y-1 mt-1.5">
      <div className="flex gap-1">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className={`h-1.5 flex-1 rounded-full transition-colors ${i <= score ? STRENGTH_COLORS[score] : "bg-[#D9D1C1]"}`} />
        ))}
      </div>
      <p className="text-xs text-[#2B2A26]/60">{STRENGTH_LABELS[score]}</p>
    </div>
  );
}

export function SecureFileOnboardingStep({
  fieldName,
  onComplete,
  onSkip,
  open,
  onOpenChange,
  fileName,
}: SecureFileOnboardingProps) {
  const session = useSensitiveDataSession();
  const [location, setLocation] = useState("");
  const [addPassphrase, setAddPassphrase] = useState(false); // opt-in: no-passphrase is the default path
  const [passphrase, setPassphrase] = useState("");
  const [showPass, setShowPass] = useState(true); // show-by-default so it can be written down

  const handleTogglePassphrase = useCallback((checked: boolean) => {
    setAddPassphrase(checked);
    // Don't retain a stale value if they type something then toggle off — the no-passphrase
    // default must mean genuinely empty (→ NO_PASSPHRASE_KEY in handleCreate).
    if (!checked) setPassphrase("");
  }, []);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSkip = useCallback(() => {
    onSkip();
    onOpenChange(false);
  }, [onSkip, onOpenChange]);

  const handleCreate = useCallback(async () => {
    setBusy(true);
    setError(null);
    try {
      const values = session.getAllValues();
      const { handle } = await saveSecureFile({ values, passphrase, fileName });
      // Persist the handle (Chrome/Edge) so Phase 3 can auto-reconnect on reload.
      if (handle) void saveHandle(handle);
      // Values are now in the file → clean. Use the same composite keys the store holds.
      session.markSaved(Object.keys(values));
      // Caller records setup (secureFileSetupAt) + persists the non-sensitive location.
      onComplete(handle, passphrase.length > 0 ? passphrase : NO_PASSPHRASE_KEY, location.trim() || undefined);
      onOpenChange(false);
    } catch (err: any) {
      if (err?.name === "AbortError") { setBusy(false); return; } // user cancelled the picker
      setError("Couldn't create your secure file. Please try again.");
      setBusy(false);
    }
  }, [session, passphrase, location, onComplete, onOpenChange, fileName]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#FEFCF8] border border-[#D9D1C1] rounded-2xl max-w-md" data-testid="secure-file-onboarding">
        <DialogHeader>
          <div className="flex flex-col items-center text-center gap-2">
            <div className="h-11 w-11 rounded-full bg-[#EEF2EA] flex items-center justify-center">
              <Lock className="h-5 w-5 text-[#4F6B51]" />
            </div>
            <DialogTitle className="font-['Fraunces'] font-medium text-lg text-[#2B2A26]">
              Set up your secure file
            </DialogTitle>
          </div>
          <DialogDescription className="text-sm text-[#2B2A26]/80 leading-relaxed text-center pt-1">
            This file stays on your computer — it's never uploaded to our servers. It holds
            your most sensitive information (SIN, passwords, PINs). Add a passphrase below to
            encrypt it so only you can open it. Create your secure file now to get started.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 pt-1">
          <div>
            <Label htmlFor="sf-location" className="text-sm font-medium text-[#2B2A26] mb-1.5 block">
              Where will you store this file?
            </Label>
            <Input
              id="sf-location"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="e.g. USB drive in my home safe, Desktop folder"
              className="bg-[#FAF7F1] border-[#D9D1C1] text-[#2B2A26] rounded-lg text-sm"
              data-testid="secure-file-location"
            />
          </div>

          <div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="sf-add-pass"
                checked={addPassphrase}
                onCheckedChange={(checked) => handleTogglePassphrase(checked === true)}
                data-testid="secure-file-add-passphrase"
              />
              <Label htmlFor="sf-add-pass" className="text-sm font-medium text-[#2B2A26] cursor-pointer">
                Add a passphrase for extra security
              </Label>
            </div>

            {addPassphrase ? (
              <div className="mt-3">
                <div className="relative">
                  <Input
                    id="sf-pass"
                    aria-label="Passphrase"
                    type={showPass ? "text" : "password"}
                    value={passphrase}
                    onChange={(e) => setPassphrase(e.target.value)}
                    placeholder="Several unrelated words work best"
                    autoComplete="new-password"
                    autoFocus
                    className="pr-10 bg-[#FAF7F1] border-[#D9D1C1] text-[#2B2A26] rounded-lg font-mono text-sm"
                    data-testid="secure-file-passphrase"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass((v) => !v)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-[#2B2A26]/40 hover:text-[#2B2A26]/70 transition-colors"
                    aria-label={showPass ? "Hide passphrase" : "Show passphrase"}
                    tabIndex={-1}
                  >
                    {showPass ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <StrengthMeter passphrase={passphrase} />
                <p className="text-xs text-[#2B2A26]/60 mt-1.5 leading-relaxed">
                  Your executor will need this passphrase to open your secure file.
                  Only add one if you're confident they'll have it when the time comes.
                </p>
              </div>
            ) : (
              <p className="text-xs text-[#2B2A26]/60 mt-1.5 leading-relaxed">
                Without a passphrase, your executor can open the file directly — nothing extra to
                find or remember.
              </p>
            )}
          </div>

          {error && (
            <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg p-2" data-testid="secure-file-error">
              {error}
            </p>
          )}

          <div className="flex items-center gap-3 pt-1">
            <Button
              onClick={handleCreate}
              disabled={busy}
              className="flex-1 bg-[#4F6B51] hover:bg-[#4F6B51]/90 text-white rounded-lg disabled:opacity-50"
              data-testid="secure-file-create"
            >
              {busy ? "Creating…" : "Create secure file"}
            </Button>
            <button
              type="button"
              onClick={handleSkip}
              className="text-sm text-[#2B2A26]/60 hover:text-[#2B2A26] transition-colors"
              data-testid="secure-file-skip"
            >
              Skip for now
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
